import React, { Fragment, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Menu, Transition } from '@headlessui/react';
import { getClassName } from 'utils/ui/classes';
import ChevronDownIcon from 'components/v2/icons/ChevronDownIcon';
import DotsVerticalIcon from 'components/v2/icons/DotsVerticalIcon';
import { useFloating, autoUpdate } from '@floating-ui/react';
const DropdownMenuButton = forwardRef(function DropdownMenuButton({ label, testDataId, disabled }, ref) {
    return (React.createElement("div", { "data-testid": testDataId || 'dropdown-button-wrapper', ref: ref },
        React.createElement(Menu.Button, { "data-testid": "undefined-f6f03ac6", disabled: disabled, className: "inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100" },
            label,
            React.createElement(ChevronDownIcon, { className: "-mr-1 ml-2 h-5 w-5", "aria-hidden": "true" }))));
});
const DropdownMenuIconButton = forwardRef(function DropdownMenuIconButton({ testDataId, disabled }, ref) {
    return (React.createElement("div", { "data-testid": testDataId || 'dropdown-button-wrapper', ref: ref },
        React.createElement(Menu.Button, { "data-testid": "undefined-64fa5550", disabled: disabled, className: "w-10 h-10 flex justify-center items-center rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100" },
            React.createElement("span", { "data-testid": "span-b456cdfc", className: "sr-only" }, "Open options"),
            React.createElement(DotsVerticalIcon, { className: "h-5 w-5", "aria-hidden": "true" }))));
});
export default function Dropdown({ label, useIcon, children, testDataId, disabled, dataTestId = 'dataTestId-Menu-Dropdown-V2', }) {
    const { refs, floatingStyles, update } = useFloating({
        strategy: 'fixed',
        placement: 'bottom-end',
        whileElementsMounted: autoUpdate,
    });
    return (React.createElement(Menu, { "data-testid": dataTestId, as: "div", className: "text-left" },
        useIcon && (React.createElement(DropdownMenuIconButton, { testDataId: testDataId || dataTestId, ref: refs.setReference, disabled: disabled })),
        !useIcon && (React.createElement(DropdownMenuButton, { label: label, testDataId: testDataId || dataTestId, ref: refs.setReference, disabled: disabled })),
        React.createElement("div", { "data-testid": "div-088916ad", ref: refs.setFloating, style: Object.assign(Object.assign({}, floatingStyles), { zIndex: 10000 }) },
            React.createElement(Transition, { as: Fragment, enter: "transition ease-out duration-100", enterFrom: "transform opacity-0 scale-95", enterTo: "transform opacity-100 scale-100", leave: "transition ease-in duration-75", leaveFrom: "transform opacity-100 scale-100", leaveTo: "transform opacity-0 scale-95", beforeEnter: () => update(), afterLeave: () => update() },
                React.createElement(Menu.Items, { "data-testid": "undefined-9f958e7c", className: "z-10 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" },
                    React.createElement("div", { "data-testid": "div-16d411dc", className: "py-1" }, children))))));
}
Dropdown.propTypes = {
    label: PropTypes.string,
    useIcon: PropTypes.bool,
    children: PropTypes.any,
    disabled: PropTypes.bool,
};
Dropdown.defaultProps = {
    label: 'Actions',
    useIcon: false,
    disabled: false,
};
Dropdown.Button = function DropdownButton({ type, onClick, children, disabled, dataTestId, }) {
    return (React.createElement(Menu.Item, { "data-testid": "undefined-9b3542ba" }, ({ active }) => (React.createElement("button", { type: type, onClick: onClick, className: getClassName('block px-4 py-2 text-sm w-full text-left', {
            'bg-gray-100 text-gray-900': active,
            'text-gray-700': !active,
            disabled: disabled,
        }), disabled: disabled, "data-testid": dataTestId || 'dropdown-option' }, children))));
};
Dropdown.Button.propTypes = {
    type: PropTypes.oneOf(['button', 'submit']),
    onClick: PropTypes.func,
    children: PropTypes.any,
    disabled: PropTypes.bool,
};
Dropdown.Button.defaultProps = {
    type: 'button',
};
Dropdown.Item = function DropdownItem({ url, label, children, target, disabled, }) {
    return (React.createElement(Menu.Item, { "data-testid": "undefined-04ae46b5" }, ({ active }) => (React.createElement("a", { "data-testid": "a-cd063b15", href: url, target: target, className: getClassName('block px-4 py-2 text-sm text-left', {
            'bg-gray-100 text-gray-900': active,
            'text-gray-700': !active,
            'pointer-events-none': disabled,
            disabled: disabled,
        }) }, children || label))));
};
Dropdown.Item.propTypes = {
    url: PropTypes.string,
    label: PropTypes.string,
    children: PropTypes.any,
    target: PropTypes.string,
    disabled: PropTypes.bool,
};
